import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../components/public/Header";
import Footer from "../../components/public/Footer";
import TrustedBy from "../../components/public/TrustedBy";
import { ArrowLeft, User, CalendarBlank, ShieldCheck, CaretDown } from "@phosphor-icons/react";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  usePageMeta({
    title: item ? (item.seo?.meta_title || `${item.title} — Bon-Heur Insights`) : "Article — Bon-Heur Pharma",
    description: item ? (item.seo?.meta_description || item.excerpt) : undefined,
    canonical: item?.seo?.canonical_url,
    ogImage: item?.cover_image_url || item?.seo?.og_image,
    ogType: "article",
    keywords: item?.seo?.meta_keywords || (item?.tags || []).join(", "),
    robots: item?.seo?.robots,
  });

  useEffect(() => {
    axios.get(`${API}/public/blogs/${slug}`).then((r) => setItem(r.data)).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true);
    });
  }, [slug]);

  useEffect(() => {
    if (!item?.jsonld) return;
    const scripts = item.jsonld.map((blk) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(blk);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => document.head.removeChild(s));
  }, [item]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-[#1F2937]">Article not found</h1>
          <button onClick={() => navigate("/blogs")} className="mt-6 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-semibold">Back to Insights</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20"><div className="h-96 bg-[#F1F5F9] rounded-2xl animate-pulse" /></div>
        <Footer />
      </div>
    );
  }

  const cover = item.cover_url ? (item.cover_url.startsWith("http") ? item.cover_url : `${BACKEND}${item.cover_url}`) : null;
  const date = item.created_at ? new Date(item.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  const reviewed = item.last_reviewed_at ? new Date(item.last_reviewed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="blog-detail">
      <Header />
      <main>
        <div className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-[#64748B]">
            <Link to="/blogs" className="inline-flex items-center gap-1.5 hover:text-[#C2410C]"><ArrowLeft size={12} /> All articles</Link>
          </div>
        </div>

        <article className="py-14 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold" style={{ animation: "blurUp 0.6s ease-out both" }}>Insight</div>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif", animation: "blurUp 0.8s ease-out 0.15s both" }}>{item.title}</h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#64748B]" style={{ animation: "blurUp 0.8s ease-out 0.25s both" }}>
              {item.author && <span className="inline-flex items-center gap-1.5"><User size={14} weight="bold" className="text-[#C2410C]" /> {item.author}{item.author_credentials ? `, ${item.author_credentials}` : ""}</span>}
              {date && <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} weight="bold" className="text-[#C2410C]" /> {date}</span>}
              {item.medical_reviewer && <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-[#0EA5E9]" /> Reviewed by {item.medical_reviewer}{reviewed ? ` · ${reviewed}` : ""}</span>}
            </div>

            {cover && (
              <div className="mt-10 aspect-[16/9] rounded-2xl overflow-hidden bg-[#F1F5F9]" style={{ animation: "blurUp 0.9s ease-out 0.35s both" }}>
                <img src={cover} alt={item.title} className="w-full h-full object-cover" />
              </div>
            )}

            {item.excerpt && <p className="mt-10 text-lg lg:text-xl text-[#475569] leading-relaxed font-medium">{item.excerpt}</p>}

            <div
              className="mt-8 prose prose-slate max-w-none text-[#1F2937] leading-relaxed whitespace-pre-line"
              style={{ fontSize: "1.0625rem", lineHeight: 1.8 }}
            >
              {item.content}
            </div>

            {item.author_bio && (
              <div className="mt-14 p-6 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0]">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">About the author</div>
                <div className="mt-2 font-semibold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.author}{item.author_credentials ? `, ${item.author_credentials}` : ""}</div>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{item.author_bio}</p>
              </div>
            )}
          </div>
        </article>

        {item.faqs?.length > 0 && (
          <section className="py-14 lg:py-20 bg-[#F8FAFC] border-t border-[#E2E8F0]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Frequently asked questions</h2>
              <div className="mt-8 space-y-3">
                {item.faqs.map((f, i) => <FaqRow key={f.id} f={f} i={i} />)}
              </div>
            </div>
          </section>
        )}
      </main>
      <TrustedBy />
      <Footer />
    </div>
  );
}

function FaqRow({ f, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="text-base font-semibold text-[#1F2937]">{f.question}</span>
        <CaretDown size={16} weight="bold" className={`text-[#C2410C] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 text-sm text-[#475569] leading-relaxed whitespace-pre-line">{f.answer}</div>
        </div>
      </div>
    </div>
  );
}
